// ============================================
// 🛟 ERROR BOUNDARY — one screen's error never blanks the whole app
// src/components/ErrorBoundary.jsx
//   <ErrorBoundary resetKey={activeTab}> … </ErrorBoundary>   (inside <main>: one screen)
//   <ErrorBoundary fullPage> <App /> </ErrorBoundary>          (index.js: last resort)
// • a crash shows "This screen couldn't load" with Try again / Reload; the sidebar and
//   header keep working, and switching screens (resetKey) clears it
// • after a deploy, an open tab asks for screen files that no longer exist
//   ("Loading chunk failed") → reload once, automatically, to get the new version
// ============================================
import React from 'react';

const CHUNK = /Loading chunk|ChunkLoadError|Loading CSS chunk|dynamically imported module|Importing a module script failed/i;
const isChunkError = (e) => CHUNK.test(`${(e && e.name) || ''} ${(e && e.message) || e || ''}`);

export default class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }

  static getDerivedStateFromError(error) { return { error }; }

  componentDidCatch(error, info) {
    console.error('[Heroshhi] screen error:', error, info && info.componentStack);
    if (isChunkError(error)) {
      // reload once (not in a loop) to pick up the newly deployed files
      try {
        const key = 'hs-chunk-reload', last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 60 * 1000) { sessionStorage.setItem(key, String(Date.now())); window.location.reload(); }
      } catch (e) { /* storage blocked: the Reload button still works */ }
    }
  }

  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const updated = isChunkError(error), full = !!this.props.fullPage;
    return (
      <div role="alert" className={full ? 'min-h-screen flex items-center justify-center p-6 bg-slate-50' : 'py-10 px-4'}>
        <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-2xl p-6 text-center">
          <p className="text-lg font-extrabold text-[#10203A]">
            {updated ? 'Heroshhi has been updated' : full ? 'Something went wrong' : "This screen couldn't load"}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            {updated ? 'Reload to get the latest version.' : 'Your data is safe. Try again, or reload the page.'}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            {!full && !updated && <button type="button" className="hs-btn hs-btn-secondary" onClick={() => this.setState({ error: null })}>Try again</button>}
            <button type="button" className="hs-btn hs-btn-primary" onClick={() => window.location.reload()}>Reload</button>
          </div>
          <p className="mt-4 text-[11px] text-gray-400 break-words">{String((error && error.message) || error).slice(0, 160)}</p>
        </div>
      </div>
    );
  }
}
